import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Zap, Database, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export function AdminJobIngest() {
  const [loading, setLoading] = useState(false);
  const [jobTitle, setJobTitle] = useState("Software Engineer");
  const [jobLocation, setJobLocation] = useState("United States");
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState<any>(null);
  const [dbStats, setDbStats] = useState<any>(null);
  const [recentJobs, setRecentJobs] = useState<any[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    loadDatabaseStats();
  }, []);

  const loadDatabaseStats = async () => {
    setStatsLoading(true);
    try {
      // Get total and active jobs
      const { data: stats } = await supabase
        .from('scraped_jobs')
        .select('id, is_active, scraped_at');

      const total = stats?.length || 0;
      const active = stats?.filter(j => j.is_active)?.length || 0;
      
      // Get jobs added today
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayJobs = stats?.filter(j => new Date(j.scraped_at) >= todayStart)?.length || 0;

      // Get oldest job date
      const oldestJob = stats?.length > 0 
        ? new Date(Math.min(...stats.map(j => new Date(j.scraped_at).getTime())))
        : null;

      setDbStats({
        total,
        active,
        todayJobs,
        oldestJob: oldestJob ? `${Math.floor((Date.now() - oldestJob.getTime()) / (1000 * 60 * 60 * 24))} days ago` : 'N/A'
      });

      // Get recent jobs
      const { data: recent } = await supabase
        .from('scraped_jobs')
        .select('title, company, location, posted_at, apply_type, job_url')
        .order('scraped_at', { ascending: false })
        .limit(10);

      setRecentJobs(recent || []);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleIngest = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      toast.info('Starting LinkedIn job ingestion... This may take 5-10 minutes.');
      
      const { data, error } = await supabase.functions.invoke('ingest-linkedin-jobs', {
        body: {
          jobTitle,
          jobLocation,
          datePosted: 'r604800', // Past week
          contractType: ['F'], // Full-time
          limit,
        }
      });

      if (error) {
        console.error('Ingest error:', error);
        throw error;
      }

      setResult(data);
      toast.success(`Successfully ingested ${data.totalIngested} Easy Apply jobs!`);
      
      // Reload stats after ingestion
      await loadDatabaseStats();
      
    } catch (error: any) {
      console.error('Job ingestion error:', error);
      toast.error(error.message || 'Failed to ingest jobs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Database Stats */}
      {!statsLoading && dbStats && (
        <Card className="p-4 bg-primary/5 border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <Database className="h-5 w-5 text-primary" />
            <h4 className="font-semibold">Database Stats</h4>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total Jobs</p>
              <p className="text-2xl font-bold">{dbStats.total}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Active Jobs</p>
              <p className="text-2xl font-bold text-green-500">{dbStats.active}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Added Today</p>
              <p className="text-2xl font-bold text-blue-500">{dbStats.todayJobs}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Oldest Job</p>
              <p className="text-lg font-semibold">{dbStats.oldestJob}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Ingestion Form */}
      <Card className="p-6">
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-green-500" />
            <h3 className="text-lg font-semibold">LinkedIn Job Ingestion (Apify)</h3>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="e.g. Software Engineer"
              />
            </div>

            <div>
              <Label htmlFor="jobLocation">Location</Label>
              <Input
                id="jobLocation"
                value={jobLocation}
                onChange={(e) => setJobLocation(e.target.value)}
                placeholder="e.g. United States"
              />
            </div>

            <div>
              <Label htmlFor="limit">Max Jobs to Scrape</Label>
              <Input
                id="limit"
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                min={10}
                max={500}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Only Easy Apply jobs will be saved to database
              </p>
            </div>
          </div>

          <Button
            onClick={handleIngest}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ingesting Jobs... (5-10 min)
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                Start Job Ingestion
              </>
            )}
          </Button>

          {result && (
            <Card className="p-4 bg-green-500/10 border-green-500/20">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                ✅ Ingestion Complete
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Total Scraped</p>
                  <p className="text-xl font-bold">{result.totalScraped}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Easy Apply Found</p>
                  <p className="text-xl font-bold text-green-600">{result.easyApplyCount}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Ingested to DB</p>
                  <p className="text-xl font-bold text-blue-600">{result.totalIngested}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Filtered Out</p>
                  <p className="text-xl font-bold text-red-600">{result.totalFiltered}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </Card>

      {/* Recent Jobs Table */}
      {recentJobs.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold">Recent Jobs in Database</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={loadDatabaseStats}
              disabled={statsLoading}
            >
              Refresh
            </Button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Posted</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentJobs.map((job, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{job.title}</TableCell>
                    <TableCell>{job.company}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{job.location}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {job.apply_type || 'Easy Apply'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <a
                        href={job.job_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        View
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
